import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import { Package, Navigation, CheckCircle, ArrowRight, Layers } from 'lucide-react';

const ROLE_INFO = {
  ROLE_CARRIER: {
    label: 'Transporteur',
    icon: <Navigation size={32} />,
    color: '#C8832A',
    bg: '#fef9f0',
    border: '#f5d9a5',
    activeBorder: '#C8832A',
    description: 'Proposez vos trajets et transportez des colis.',
    features: [
      'Publier des annonces de trajets',
      'Recevoir des demandes d\'expéditeurs',
      'Gérer vos missions de livraison',
      'Accéder à votre tableau de bord transporteur',
    ],
  },
  ROLE_SENDER: {
    label: 'Expéditeur',
    icon: <Package size={32} />,
    color: '#0e7a68',
    bg: '#ecfdf5',
    border: '#a7f3d0',
    activeBorder: '#0e7a68',
    description: 'Publiez vos colis et trouvez un transporteur.',
    features: [
      'Publier des annonces de colis',
      'Trouver des transporteurs disponibles',
      'Suivre vos envois en temps réel',
      'Accéder à votre tableau de bord expéditeur',
    ],
  },
};

export default function RoleSwitchPage() {
  const { user, switchRole } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(null);

  const userRoles = user?.roles || [];
  const activeRole = user?.active_role;

  const handleSwitch = async (role) => {
    if (role === activeRole) return;
    setSwitching(role);
    try {
      await switchRole(role);
      const info = ROLE_INFO[role];
      toast.success(`Profil basculé en mode ${info?.label}. Bienvenue !`);
      navigate('/app/dashboard');
    } catch {
      toast.error('Erreur lors du changement de profil.');
    } finally {
      setSwitching(null);
    }
  };

  const isHybrid = userRoles.includes('ROLE_CARRIER') && userRoles.includes('ROLE_SENDER');

  if (!isHybrid) {
    return (
      <div style={{ maxWidth: 520, margin: '60px auto', textAlign: 'center', padding: '0 16px' }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', background: '#f3f4f6',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px',
        }}>
          <Layers size={28} color="#9CA3AF" />
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
          Fonctionnalité multi-rôles
        </div>
        <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 24 }}>
          Vous devez avoir les rôles Transporteur et Expéditeur pour accéder à cette page.
        </div>
        <button
          onClick={() => navigate('/app/role-requests')}
          style={{
            background: '#0e7a68', color: '#fff', border: 'none',
            borderRadius: 10, padding: '12px 24px', fontSize: 14,
            fontWeight: 600, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}
        >
          Demander un rôle <ArrowRight size={16} />
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <div className="mlc-topbar" style={{ marginBottom: 8 }}>
        <div>
          <div className="c2s-topbar-title">Changer de profil</div>
          <div className="c2s-topbar-sub">Vous avez les deux rôles. Choisissez le profil actif</div>
        </div>
      </div>

      {/* Badge actif */}
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        background: '#f0fdf4', border: '1px solid #bbf7d0',
        borderRadius: 20, padding: '6px 14px', marginBottom: 28, fontSize: 13,
        color: '#065f46', fontWeight: 600,
      }}>
        <CheckCircle size={14} />
        Profil actif : {ROLE_INFO[activeRole]?.label || activeRole}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 20 }}>
        {Object.entries(ROLE_INFO).map(([role, info]) => {
          const isActive = role === activeRole;
          const isSwitching = switching === role;

          return (
            <div
              key={role}
              style={{
                border: `2px solid ${isActive ? info.activeBorder : '#E5E7EB'}`,
                borderRadius: 16, padding: '24px 22px',
                background: isActive ? info.bg : '#fff',
                display: 'flex', flexDirection: 'column', gap: 18,
                transition: 'border-color 0.2s, box-shadow 0.2s',
                boxShadow: isActive ? `0 0 0 4px ${info.border}` : 'none',
              }}
            >
              {/* En-tête */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{
                    color: info.color, background: info.bg,
                    borderRadius: 12, padding: 12, lineHeight: 1,
                    border: `1.5px solid ${info.border}`,
                  }}>
                    {info.icon}
                  </span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 17, color: '#111827' }}>
                      {info.label}
                    </div>
                    <div style={{ fontSize: 12.5, color: '#6B7280', marginTop: 2 }}>
                      {info.description}
                    </div>
                  </div>
                </div>
                {isActive && (
                  <span style={{
                    background: info.color, color: '#fff',
                    borderRadius: 20, padding: '4px 12px', fontSize: 11, fontWeight: 700,
                    whiteSpace: 'nowrap', marginLeft: 8,
                  }}>
                    Actif
                  </span>
                )}
              </div>

              {/* Fonctionnalités */}
              <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {info.features.map((f, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151' }}>
                    <CheckCircle size={14} color={info.color} style={{ flexShrink: 0 }} />
                    {f}
                  </li>
                ))}
              </ul>

              {/* Action */}
              {isActive ? (
                <div style={{
                  textAlign: 'center', padding: '10px',
                  borderRadius: 10, background: info.border,
                  fontSize: 13.5, fontWeight: 600, color: info.color,
                }}>
                  Profil actuellement actif
                </div>
              ) : (
                <button
                  onClick={() => handleSwitch(role)}
                  disabled={!!switching}
                  style={{
                    background: info.color, color: '#fff', border: 'none',
                    borderRadius: 10, padding: '12px', fontSize: 14,
                    fontWeight: 600, cursor: switching ? 'wait' : 'pointer',
                    opacity: switching && !isSwitching ? 0.6 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    transition: 'opacity 0.2s',
                  }}
                >
                  {isSwitching ? (
                    'Changement en cours...'
                  ) : (
                    <>Passer en mode {info.label} <ArrowRight size={16} /></>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Infos */}
      <div style={{
        marginTop: 32, padding: '14px 18px',
        background: '#f9fafb', borderRadius: 12, border: '1px solid #E5E7EB',
        fontSize: 13, color: '#6B7280', lineHeight: 1.7,
      }}>
        <strong style={{ color: '#374151' }}>Comment ça fonctionne ?</strong><br />
        En changeant de profil, votre tableau de bord, vos annonces et vos menus s'adaptent au rôle choisi. Vous pouvez basculer à tout moment depuis la barre latérale.
      </div>
    </div>
  );
}
